import React from 'react'

import {
  useBrandColor,
  useBrandGraphic,
} from '../../../../Designs/blocs/brandResolvers'
import { useLastActiveMediaTrayCard } from '../../../../firebase/hooks/useTray'
import type { FeedMedia, Mix } from '../../../../firebase/model/mix'
import type { DeepReadonly } from '../../../../firebase/typeUtils'
import { VideoTrack } from '../../../../MediaTracks/components/VideoTrack/VideoTrack'
import { FloorTrayImage } from '../../../../Stage/floor/FloorFeed/FloorTrayFeed/FloorTrayImage/FloorTrayImage'
import { StaticElementsForMix } from '../../../../Stage/floor/StaticElements/StaticElements'
import { posToStyle } from '../../../../Stage/layouts'
import {
  useHeadlessParticipant,
  useParticipant,
} from '../../../../webrtc/webRTCRoom'

import './MixPreview.scss'

interface LayoutPreviewProps {
  mix: DeepReadonly<Mix>
}
const MixPreview = ({ mix }: LayoutPreviewProps) => {
  const toBrandColor = useBrandColor()
  const toBrandGraphic = useBrandGraphic()

  const backgroundGraphic = toBrandGraphic(mix.settings?.backgroundImage)

  return (
    <div
      className="mix-preview"
      style={{
        '--mix-background-color': toBrandColor(mix.settings?.backgroundColor),
        backgroundImage: backgroundGraphic
          ? `url(${backgroundGraphic})`
          : undefined,
      }}
    >
      <div>
        {mix.feeds.map((f, idx) => (
          <div
            className="mix-preview-feed-positioner"
            // eslint-disable-next-line react/no-array-index-key
            key={idx}
            style={posToStyle(f)}
          >
            <Track track={f.media} />
          </div>
        ))}
        <StaticElementsForMix mix={mix} />
      </div>
    </div>
  )
}

const Track = ({ track }: { track: DeepReadonly<FeedMedia> | undefined }) => {
  if (!track) return null
  if (track.type === 'tray') {
    return <TrayTrack />
  }
  return <ParticipantTrack type={track.type} participantId={track.id} />
}

const ParticipantTrack = ({
  type,
  participantId,
}: {
  type: 'camera' | 'screen' | 'mobile'
  participantId: number
}) => {
  const participant = useParticipant(participantId)

  if (!participant) return null

  const mediaTrack = participant.tracks[type]

  if (!mediaTrack) return null

  return <VideoTrack track={mediaTrack} isMimic />
}

const TrayTrack = () => {
  const lastActiveMediaCard = useLastActiveMediaTrayCard()

  const headlessParticipant = useHeadlessParticipant()

  return (
    <div className="tray-track">
      {lastActiveMediaCard?.type === 'image' ? (
        <FloorTrayImage trayCard={lastActiveMediaCard} />
      ) : (
        <>
          {lastActiveMediaCard?.type === 'video' &&
            headlessParticipant?.tracks.screen && (
              <VideoTrack track={headlessParticipant.tracks.screen} isMimic />
            )}
        </>
      )}
    </div>
  )
}

export { MixPreview }
